import {
	type ActorDto,
	AuthService,
	BillingService,
	type ConversationAct,
	ConversationService,
	type CreateTalkingHeadActorRequest,
	CustomerService,
	type TalkingHeadActorCloneRequest,
	TalkingHeadActorEventIdn,
	type TalkingHeadActorEventRequest,
	TalkingHeadService,
	type TBillingCheckoutUrlData,
	type UtilRequestConfig
} from '@newo/api-client';
import { CreatorAgentSuccessFlowStepKey as AgentSuccessStep } from '@newo/tenant-metadata';
import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { CLIENT_SECRET, runtimeConfig } from '@/constants';
import { partnerMetadata } from '@/services/PartnerMetadata';
import { useAgentTraining } from '@/store/agent-training.ts';
import useAppStore from '@/store/app.ts';

export type InitSuccessCb = {
	clientSecret: string;
	externalId: string;
	connectorIdn: string;
};

export const useActorCreateMutation = () =>
	useMutation({
		mutationFn: (data: CreateTalkingHeadActorRequest) =>
			TalkingHeadService.createActor(data, {
				...(runtimeConfig.isRemoteBuild ? {} : { skipAuth: true })
			})
	});

export type SendActMessageInput = {
	message: string;
	actorId: string;
	externalEventId: string;
	clientSecret: string;
};

export const useActorSendEventMutation = () =>
	useMutation({
		mutationFn: (data: TalkingHeadActorEventRequest) =>
			TalkingHeadService.sendActorEvent(data, {
				...(runtimeConfig.isRemoteBuild ? {} : { skipAuth: true })
			})
	});

export const useSendUserMessageMutation = () =>
	useMutation({
		mutationFn: ({
			message,
			actorId,
			externalEventId,
			clientSecret
		}: SendActMessageInput) => {
			return TalkingHeadService.sendMessage({
				client_secret: clientSecret,
				connector_idn: 'newo_chat',
				event_idn: TalkingHeadActorEventIdn.UserMessage,
				external_id: externalEventId,
				text: message,
				user_actor_id: actorId
			});
		}
	});

export const useGetActMessages = () =>
	useMutation({
		mutationKey: ['acts'],
		mutationFn: async (actorId?: string): Promise<ConversationAct[]> => {
			const data = await ConversationService.getActs({
				user_actor_id: actorId ?? ''
			});

			return data.items ?? [];
		}
	});

export const initialize = async (
	isFirstStep: boolean,
	actor?: Pick<ActorDto, 'external_id'>
) => {
	const agentSuccessConfig = partnerMetadata.getAgentSuccessConfig();
	const initialExternalId = isFirstStep
		? uuidv4()
		: (useAppStore.getState().externalId ?? uuidv4());
	const externalId = actor?.external_id ?? initialExternalId;
	const talkingHeadRequestConfig = {
		...(runtimeConfig.isRemoteBuild ? {} : { skipAuth: true })
	};
	const settings = await TalkingHeadService.getSettings(
		{ client_secret: CLIENT_SECRET },
		talkingHeadRequestConfig
	);

	const actorRequest = {
		name: 'Agent Creator App Actor',
		external_id: externalId,
		connector_idn: settings.connector_idn,
		client_secret: settings.client_secret
	};
	const actorResponse = await TalkingHeadService.createActor(
		actorRequest,
		talkingHeadRequestConfig
	);

	if (partnerMetadata.getId() === 'newo') {
		const cloneRequest: TalkingHeadActorCloneRequest = {
			external_id: externalId,
			connector_idn: settings.connector_idn,
			client_secret: settings.client_secret,
			to_connector_idn: 'newo_voice_connector',
			to_integration_idn: 'newo_voice'
		};

		TalkingHeadService.cloneActorToVoice(
			cloneRequest,
			talkingHeadRequestConfig
		).catch((err) => console.warn('cloneActorToVoice failed:', err));
	}

	const firstKey =
		agentSuccessConfig.stepOrder?.[0] ?? AgentSuccessStep.ActivateAccount;
	const storedStep = useAppStore.getState().agentSuccessActiveStep;

	const activeStep =
		storedStep && storedStep in agentSuccessConfig.steps
			? storedStep
			: firstKey;
	useAppStore.getState().setAppState({
		clientSecret: settings.client_secret,
		connectorIdn: settings.connector_idn,
		externalId,
		agentSuccessActiveStep: activeStep
	});

	if (isFirstStep) {
		useAgentTraining.getState().resetStatePreservePartner();
	}

	useAgentTraining.getState().setActor(actorResponse);

	return {
		externalId,
		clientSecret: settings.client_secret,
		connectorIdn: settings.connector_idn,
		actorId: actorResponse.id
	};
};

export const useActivateAccount = () =>
	useMutation({
		mutationFn: async ({
			customerIdn,
			productKey,
			promotionCode
		}: {
			customerIdn: string;
			productKey: string;
			promotionCode?: string | null;
		}): Promise<string> => {
			const data: TBillingCheckoutUrlData = {
				customer_idn: customerIdn,
				product_key: productKey,
				success_url:
					partnerMetadata.getCheckoutSuccessUrl() ||
					`${globalThis.location.href}?checkout=session_completed`,
				...(promotionCode ? { promotion_code: promotionCode } : {}),
				development_stage_enabled: false,
				trial_enabled: false
			};

			const response = await BillingService.getBillingCheckoutUrl(data);

			return response.url;
		}
	});

export const useInitialize = (
	isFirstStep: boolean,
	actor?: Pick<ActorDto, 'external_id'>
) => {
	return useMutation({ mutationFn: () => initialize(isFirstStep, actor) });
};

export const initializeVibeCreator = async () => {
	const agentSuccessConfig = partnerMetadata.getAgentSuccessConfig();
	const appState = useAppStore.getState();
	const externalId = appState.actor?.external_id ?? uuidv4();
	const settings = await TalkingHeadService.getSettings({
		client_secret: CLIENT_SECRET
	});

	const actorResponse = await TalkingHeadService.createActor({
		name: 'Agent Creator App Actor',
		external_id: externalId,
		connector_idn: settings.connector_idn,
		client_secret: settings.client_secret
	});

	if (partnerMetadata.getId() === 'newo') {
		TalkingHeadService.cloneActorToVoice({
			external_id: externalId,
			connector_idn: settings.connector_idn,
			client_secret: settings.client_secret,
			to_connector_idn: 'newo_voice_connector',
			to_integration_idn: 'newo_voice'
		}).catch((err) => console.warn('cloneActorToVoice failed:', err));
	}

	const firstKey =
		agentSuccessConfig.stepOrder?.[0] ?? AgentSuccessStep.ActivateAccount;
	const storedStep = appState.agentSuccessActiveStep;
	const activeStep =
		storedStep && storedStep in agentSuccessConfig.steps
			? storedStep
			: firstKey;

	appState.setAppState({
		clientSecret: settings.client_secret,
		connectorIdn: settings.connector_idn,
		externalId,
		agentSuccessActiveStep: activeStep
	});

	useAgentTraining.getState().setActor(actorResponse);

	return {
		externalId,
		clientSecret: settings.client_secret,
		connectorIdn: settings.connector_idn,
		actorId: actorResponse.id
	};
};

export const useInitializeVibeCreator = () => {
	return useMutation({ mutationFn: initializeVibeCreator });
};

export const useGenerateCustomerToken = () =>
	useMutation({
		mutationFn: async ({
			customerId,
			axiosConfig
		}: {
			customerId: string;
			axiosConfig?: UtilRequestConfig;
		}) =>
			AuthService.generateCustomerToken(
				{
					to_customer_id: customerId
				},
				axiosConfig
			)
	});

export const useGetCustomerByIdn = () => {
	return useMutation({
		mutationFn: async ({
			customerIdn,
			axiosConfig
		}: {
			customerIdn: string;
			axiosConfig?: UtilRequestConfig;
		}) => {
			return await CustomerService.getCustomerByIdn(customerIdn, axiosConfig);
		}
	});
};
